// Mechanical Section - Enhanced Detail View with Technical Drawing and Flip Cards
import { useEffect, useState } from 'preact/hooks'
import { MECHANICAL } from '../../../data/definitions.js'
import { getSvgContent } from '../../../modules/dataService.js'
import { formatValue as formatValueUtil } from '../../../utils/formatUtils.js'
// import CellMechanicalDrawing from '../../visualizations/CellMechanicalDrawing.jsx' // TODO: Improve and re-implement

export default function MechanicalSection({ cellData, imageUrl, watermark }) {
  if (!cellData) return null

  const [flippedCards, setFlippedCards] = useState({})
  const [technicalDrawingSvg, setTechnicalDrawingSvg] = useState(null)
  const [drawingError, setDrawingError] = useState(false)
  const [drawingLoading, setDrawingLoading] = useState(true)

  // Load technical drawing SVG content with caching (instant on revisit)
  useEffect(() => {
    if (!cellData?.id) {
      setDrawingLoading(false)
      return
    }

    setDrawingLoading(true)
    setDrawingError(false)

    let cancelled = false

    const loadDrawing = async () => {
      try {
        // Use cached SVG fetcher - instant on revisit, deduplicated requests
        const svgText = await getSvgContent(cellData.id, 'report_drawing_exterior.svg')

        if (!cancelled) {
          if (svgText) {
            setTechnicalDrawingSvg(svgText)
            setDrawingError(false)
          } else {
            setDrawingError(true)
          }
          setDrawingLoading(false)
        }
      } catch (err) {
        console.error('[MechanicalSection] Failed to load technical drawing:', err)
        if (!cancelled) {
          setDrawingError(true)
          setDrawingLoading(false)
        }
      }
    }

    loadDrawing()

    return () => {
      cancelled = true
    }
  }, [cellData?.id])

  const toggleFlip = (cardId) => {
    setFlippedCards(prev => ({ ...prev, [cardId]: !prev[cardId] }))
  }

  const mech = cellData.mechanical || {}
  const chem = cellData.chemical || {}
  const safety = cellData.safety || {}

  // Format helpers using global formatValue
  const formatValue = (value, unit = '', property = null) => {
    if (value == null || value === '' || isNaN(value)) return '–'
    return `${formatValueUtil(value, unit, property)} ${unit}`.trim()
  }

  // Helper for array values (pole dimensions)
  const formatArrayValue = (arr, index, unit = '', property = null) => {
    if (!Array.isArray(arr) || arr[index] == null || isNaN(arr[index])) return '–'
    return `${formatValueUtil(arr[index], unit, property)} ${unit}`.trim()
  }

  // Format value with separate number and unit (for proper typography)
  const formatValueWithUnit = (value, unit = '', property = null) => {
    if (value == null || value === '' || isNaN(value)) {
      return <span className="spec-value">–</span>
    }
    return (
      <span className="spec-value">
        <span className="value-num">{formatValueUtil(value, unit, property)}</span>
        {unit && <span className="value-unit">{unit}</span>}
      </span>
    )
  }

  // Format array value with separate number and unit
  const formatArrayValueWithUnit = (arr, index, unit = '', property = null) => {
    if (!Array.isArray(arr) || arr[index] == null || isNaN(arr[index])) {
      return <span className="spec-value">–</span>
    }
    return (
      <span className="spec-value">
        <span className="value-num">{formatValueUtil(arr[index], unit, property)}</span>
        {unit && <span className="value-unit">{unit}</span>}
      </span>
    )
  }

  // Check if both pole dimensions are equal
  const arePolesDifferent = (arr) => {
    if (!Array.isArray(arr) || arr.length < 2) return false
    return arr[0] !== arr[1]
  }

  // Format string with fallback
  const shape = mech.shape || '–' // cylindrical, prismatic, pouch
  const format = mech.format || '–' // 18650, 21700, etc.
  const formatGroup = mech.format_group || '–' // cylindrical, prismatic, pouch
  const combinedFormat = formatGroup !== '–' && format !== '–'
    ? `${format} (${formatGroup})`
    : format !== '–' ? format : formatGroup !== '–' ? formatGroup : '–'

  // Dimensions text
  const dimensionsString = mech.dimensions_text || '–'
  const poleDimensionsString = mech.pole_dimensions_text || '–'

  // Simplified pole location (remove (x) or (y) suffix)
  const poleLocation = mech.pole_location
    ? mech.pole_location.replace(/\s*\([xy]\)/gi, '').trim()
    : '–'

  // Calculate density (g/cm³)
  const density = (mech.mass_g && mech.vol_cell_cm3)
    ? formatValueUtil(mech.mass_g / mech.vol_cell_cm3, 'g/cm³', 'density')
    : null

  // Calculate A/V ratio (cm⁻¹)
  const avRatio = (mech.area_cm2 && mech.vol_cell_cm3)
    ? formatValueUtil(mech.area_cm2 / mech.vol_cell_cm3, 'cm⁻¹', 'surface_to_volume_ratio')
    : null

  // Determine cell type for dimension display
  const isCylindrical = shape?.toLowerCase().includes('cylindrical')
  const isPouch = shape?.toLowerCase().includes('pouch')
  const isPrismatic = shape?.toLowerCase().includes('prismatic')

  // Process SVG to ensure it displays correctly
  // LibreOffice SVGs often have large dimensions or no viewBox - we need to fix them
  const processedSvg = technicalDrawingSvg ? (() => {
    let svg = technicalDrawingSvg

    // Remove XML declaration and DOCTYPE - they break inline HTML rendering
    svg = svg.replace(/<\?xml[^?]*\?>\s*/gi, '')
    svg = svg.replace(/<!DOCTYPE[^>]*>\s*/gi, '')

    // CRITICAL: LibreOffice SVGs wrap the main content in <g visibility="hidden">
    // This makes the entire drawing invisible - we need to remove these hidden attributes
    svg = svg.replace(/(<g[^>]*)\svisibility\s*=\s*["']hidden["']/gi, '$1')

    // Extract viewBox if present, otherwise create one from width/height
    const viewBoxMatch = svg.match(/viewBox\s*=\s*["']([^"']+)["']/i)
    const widthMatch = svg.match(/<svg[^>]*\swidth\s*=\s*["']([^"']+)["']/i)
    const heightMatch = svg.match(/<svg[^>]*\sheight\s*=\s*["']([^"']+)["']/i)

    // If there's no viewBox but there are width/height, create a viewBox
    if (!viewBoxMatch && widthMatch && heightMatch) {
      const w = parseFloat(widthMatch[1])
      const h = parseFloat(heightMatch[1])
      if (!isNaN(w) && !isNaN(h)) {
        svg = svg.replace(/<svg([^>]*)>/, `<svg$1 viewBox="0 0 ${w} ${h}">`)
      }
    }

    // Clean up SVG tag: remove fixed dimensions but keep viewBox intact
    // The SVG will scale naturally based on its viewBox and container CSS
    svg = svg.replace(/<svg([^>]*)>/i, (match, attrs) => {
      // Remove standalone width="..." and height="..." attributes (not stroke-width!)
      let cleanAttrs = attrs
        .replace(/\swidth\s*=\s*["'][^"']*["']/gi, ' ')
        .replace(/\sheight\s*=\s*["'][^"']*["']/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()

      // Add class for CSS styling - NO width="100%" to preserve native stroke rendering
      // The container CSS will handle responsive sizing
      return `<svg ${cleanAttrs} class="technical-drawing-svg-content">`
    })

    return svg
  })() : null

  return (
    <div className="mechanical-section-new" data-tour="mechanical-section">
      {/* Visual Section - Technical Drawing (Full Width) */}
      <div className="visual-section mechanical-visuals">
        <div className="visual-card drawing-card full-width">
          <h3 className="card-title">Technical Drawing</h3>
          <div className="drawing-container">
            {processedSvg ? (
              <div
                className="technical-drawing-svg"
                dangerouslySetInnerHTML={{ __html: processedSvg }}
              />
            ) : drawingError ? (
              <div className="drawing-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 15l6-6 4 4 8-8" />
                </svg>
                <p>Technical drawing not available</p>
              </div>
            ) : drawingLoading ? (
              <div className="loading-spinner loading-spinner--small">
                <div className="loading-spinner__animation">
                  <div className="loading-spinner__ring"></div>
                  <div className="loading-spinner__core"></div>
                </div>
                <div className="loading-spinner__text">Loading drawing...</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Data Cards Grid - styled like Fact Sheet cards */}
      <div className="fact-sheet-cards mechanical-data-cards">

        {/* Card 1: Cell Dimensions - FLIP CARD */}
        <div className={`fact-card flipcard ${flippedCards['dimensions'] ? 'flipped' : ''}`}>
          <div className="flipcard-inner">
            <div className="flipcard-front">
              <button className="flip-btn" onClick={() => toggleFlip('dimensions')} title="Show info">?</button>
              <h3 className="column-title">Cell Dimensions</h3>
              <div className="spec-list-minimal">
                <div className="spec-row">
                  <span className="spec-label">Format</span>
                  <span className="spec-value">{format}</span>
                </div>
                {isCylindrical ? (
                  <>
                    <div className="spec-row">
                      <span className="spec-label">Diameter (2r)</span>
                      {formatValueWithUnit(mech.r_cell_mm ? mech.r_cell_mm * 2 : null, 'mm', 1)}
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Radius (r)</span>
                      {formatValueWithUnit(mech.r_cell_mm, 'mm', 2)}
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Height (z)</span>
                      {formatValueWithUnit(mech.z_cell_mm, 'mm', 1)}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="spec-row">
                      <span className="spec-label">Length (x)</span>
                      {formatValueWithUnit(mech.x_cell_mm, 'mm', 1)}
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Width (y)</span>
                      {formatValueWithUnit(mech.y_cell_mm, 'mm', 1)}
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Height (z)</span>
                      {formatValueWithUnit(mech.z_cell_mm, 'mm', 1)}
                    </div>
                  </>
                )}
                {isPouch && (
                  <>
                    <div className="spec-row">
                      <span className="spec-label">Core Length (x)</span>
                      {formatValueWithUnit(mech.x_pouchcore_mm, 'mm', 1)}
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Core Width (y)</span>
                      {formatValueWithUnit(mech.y_pouchcore_mm, 'mm', 1)}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flipcard-back">
              <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('dimensions')} title="Close">×</button>
              <h3 className="column-title">{MECHANICAL.cellDimensions.title}</h3>
              <div className="flip-content">
                <p>{MECHANICAL.cellDimensions.text}</p>
                <ul>
                  {MECHANICAL.cellDimensions.items.map(item => (
                    (item.label !== 'Core' || isPouch) && (
                      <li key={item.label}><strong>{item.label}:</strong> {item.description}</li>
                    )
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Tab/Pole Dimensions - FLIP CARD */}
        <div className={`fact-card flipcard ${flippedCards['poles'] ? 'flipped' : ''}`}>
          <div className="flipcard-inner">
            <div className="flipcard-front">
              <button className="flip-btn" onClick={() => toggleFlip('poles')} title="Show info">?</button>
              <h3 className="column-title">Pole Dimensions</h3>
              <div className="spec-list-minimal">
                {isCylindrical ? (
                  <>
                    <div className="spec-row">
                      <span className="spec-label">Pole Radius (r)</span>
                      {formatArrayValueWithUnit(mech.r_pole_mm, 0, 'mm', 2)}
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Pole Height (z)</span>
                      {formatArrayValueWithUnit(mech.z_pole_mm, 0, 'mm', 1)}
                    </div>
                  </>
                ) : (
                  <>
                    {arePolesDifferent(mech.x_pole_mm) ? (
                      <>
                        <div className="spec-row">
                          <span className="spec-label">Pole 1 Length (x)</span>
                          {formatArrayValueWithUnit(mech.x_pole_mm, 0, 'mm', 1)}
                        </div>
                        <div className="spec-row">
                          <span className="spec-label">Pole 2 Length (x)</span>
                          {formatArrayValueWithUnit(mech.x_pole_mm, 1, 'mm', 1)}
                        </div>
                      </>
                    ) : (
                      <div className="spec-row">
                        <span className="spec-label">Pole Length (x)</span>
                        {formatArrayValueWithUnit(mech.x_pole_mm, 0, 'mm', 1)}
                      </div>
                    )}
                    {arePolesDifferent(mech.y_pole_mm) ? (
                      <>
                        <div className="spec-row">
                          <span className="spec-label">Pole 1 Width (y)</span>
                          {formatArrayValueWithUnit(mech.y_pole_mm, 0, 'mm', 1)}
                        </div>
                        <div className="spec-row">
                          <span className="spec-label">Pole 2 Width (y)</span>
                          {formatArrayValueWithUnit(mech.y_pole_mm, 1, 'mm', 1)}
                        </div>
                      </>
                    ) : (
                      <div className="spec-row">
                        <span className="spec-label">Pole Width (y)</span>
                        {formatArrayValueWithUnit(mech.y_pole_mm, 0, 'mm', 1)}
                      </div>
                    )}
                    {arePolesDifferent(mech.z_pole_mm) ? (
                      <>
                        <div className="spec-row">
                          <span className="spec-label">Pole 1 Height (z)</span>
                          {formatArrayValueWithUnit(mech.z_pole_mm, 0, 'mm', 1)}
                        </div>
                        <div className="spec-row">
                          <span className="spec-label">Pole 2 Height (z)</span>
                          {formatArrayValueWithUnit(mech.z_pole_mm, 1, 'mm', 1)}
                        </div>
                      </>
                    ) : (
                      <div className="spec-row">
                        <span className="spec-label">Pole Height (z)</span>
                        {formatArrayValueWithUnit(mech.z_pole_mm, 0, 'mm', 1)}
                      </div>
                    )}
                  </>
                )}
                <div className="spec-row">
                  <span className="spec-label">{isCylindrical ? 'Pole' : 'Tab'} Location</span>
                  <span className="spec-value">{poleLocation}</span>
                </div>
              </div>
            </div>
            <div className="flipcard-back">
              <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('poles')} title="Close">×</button>
              <h3 className="column-title">{MECHANICAL.poleDimensions.title}</h3>
              <div className="flip-content">
                <p>{MECHANICAL.poleDimensions.text}</p>
                <ul>
                  {MECHANICAL.poleDimensions.items.map(item => (
                    <li key={item.label}><strong>{item.label}:</strong> {item.description}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Physical Properties - FLIP CARD */}
        <div className={`fact-card flipcard ${flippedCards['physical'] ? 'flipped' : ''}`}>
          <div className="flipcard-inner">
            <div className="flipcard-front">
              <button className="flip-btn" onClick={() => toggleFlip('physical')} title="Show info">?</button>
              <h3 className="column-title">Physical Properties</h3>
              <div className="spec-list-minimal">
                <div className="spec-row">
                  <span className="spec-label">Mass</span>
                  {formatValueWithUnit(mech.mass_g, 'g', 1)}
                </div>
                <div className="spec-row">
                  <span className="spec-label">Volume</span>
                  {formatValueWithUnit(mech.vol_cell_cm3, 'cm³', 2)}
                </div>
                <div className="spec-row">
                  <span className="spec-label">Surface Area</span>
                  {formatValueWithUnit(mech.area_cm2, 'cm²', 1)}
                </div>
              </div>
            </div>
            <div className="flipcard-back">
              <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('physical')} title="Close">×</button>
              <h3 className="column-title">{MECHANICAL.physicalProperties.title}</h3>
              <div className="flip-content">
                <p>{MECHANICAL.physicalProperties.text}</p>
                <ul>
                  {MECHANICAL.physicalProperties.items.map(item => (
                    <li key={item.label}><strong>{item.label}:</strong> {item.description}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Card 4: Safety Features - FLIP CARD */}
        <div className={`fact-card flipcard ${flippedCards['safety'] ? 'flipped' : ''}`}>
          <div className="flipcard-inner">
            <div className="flipcard-front">
              <button className="flip-btn" onClick={() => toggleFlip('safety')} title="Show info">?</button>
              <h3 className="column-title">Safety Features</h3>
              {cellData?.general?.internal_analysis_available === false ? (
                <div className="spec-list-minimal" style={{ padding: '20px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ea5b0c" strokeWidth="2" style={{ flexShrink: 0 }}>
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                      <circle cx="11" cy="11" r="3" fill="#ea5b0c" fillOpacity="0.2" />
                    </svg>
                    <span style={{ color: '#ea5b0c', fontSize: '14px', fontWeight: '600', fontFamily: 'Big John, sans-serif', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Analysis Ongoing
                    </span>
                  </div>
                  <div style={{ color: '#999', fontSize: '12px' }}>
                    Cell not yet opened for internal inspection
                  </div>
                </div>
              ) : (
                <div className="spec-list-minimal">
                  <div className="spec-row">
                    <span className="spec-label">Current Interrupt Device</span>
                    <span className="spec-value">{safety.CID ? '✓' : '✗'}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">PTC Resistor</span>
                    <span className="spec-value">{safety.PTC ? '✓' : '✗'}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">Internal Short Protection</span>
                    <span className="spec-value">{safety.ISP ? '✓' : '✗'}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">Safety Vent</span>
                    <span className="spec-value">{safety.VENT ? '✓' : '✗'}</span>
                  </div>
                  <div className="spec-row">
                    <span className="spec-label">Burst Valve</span>
                    <span className="spec-value">{safety.BURST ? '✓' : '✗'}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="flipcard-back">
              <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('safety')} title="Close">×</button>
              <h3 className="column-title">{MECHANICAL.safetyFeatures.title}</h3>
              <div className="flip-content">
                <p>{MECHANICAL.safetyFeatures.text}</p>
                <ul>
                  {MECHANICAL.safetyFeatures.items.map(item => (
                    <li key={item.label}><strong>{item.label}:</strong> {item.description}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
